import { hasYesOrNo, checkValue } from 'models/validate';
import offenseAddress from 'forms/shared/validators/models/location/models/offense';
import chargeValidator from 'forms/shared/validators/models/charge/charge';
import legalSentenceValidator from 'forms/sections/legal/shared/sentence/sentence';
import { DEFAULT_EARLIEST, DEFAULT_LATEST } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { getSafeDateTime, setToStartOfMonth } from 'helpers/date';
import { isPVQForm } from 'helpers/form';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { LocationModel } from 'forms/shared/types';
import { DeepSafePartial } from 'types/types';
import { isSafeUnitedStates } from 'helpers/locations';
import { DateTime } from 'luxon';
import { LegalPoliceOffenseItemModel } from '../types';

export const legalPoliceOffenseItemValidator: ValidateJSModel<LegalPoliceOffenseItemModel> = {
  CaseClosed: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  CaseClosedDate: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes.CaseClosed?.value === 'Yes') {
      return {
        date: {
          earliest: getSafeDateTime(setToStartOfMonth(options.applicantBirthdate || DEFAULT_EARLIEST) as DateTime).datetime,
          latest: DEFAULT_LATEST,
          requireDay: false,
          tooEarly: 'error.applicantMin',
        },
        presence: true,
      };
    }

    return {};
  },
  CaseClosedStatusExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes.CaseClosed?.value === 'No') {
      return { hasValue: true };
    }

    return {};
  },
  // Validation is the same for both PVQ and non-PVQ
  Date: (value, attributes, attributeName, options) => {
    const applicantBirthdate = getSafeDateTime(setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST) as DateTime);
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    return {
      presence: true,
      date: {
        earliest: applicantBirthdate.datetime,
        latest: DEFAULT_LATEST,
        tooEarly: 'error.applicantMin',
      },
    };
  },
  Description: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    if (isPVQForm(options?.formType)) {
      if (options.requireLegalOffenseInvolvements) {
        return { presence: true, hasValue: true };
      }

      return {};
    }

    return { presence: true, hasValue: true };
  },
  // Validation is the same for both PVQ and non-PVQ
  InvolvedViolence: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    if (options?.requireLegalOffenseInvolvements) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }
    return {};
  },
  // Validation is the same for both PVQ and non-PVQ
  InvolvedFirearms: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    if (options?.requireLegalOffenseInvolvements) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }
    return {};
  },
  // Validation is the same for both PVQ and non-PVQ
  InvolvedSubstances: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    if (options?.requireLegalOffenseInvolvements) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }
    return {};
  },
  InvolvedExplosives: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    if (isPVQForm(options?.formType)
      && options?.requireLegalOffenseInvolvements) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }

    return {};
  },
  InvolvedAlcohol: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    if (isPVQForm(options?.formType)
      && options?.requireLegalOffenseInvolvements) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }

    return {};
  },
  Address: (value: DeepSafePartial<LocationModel>, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    if (isPVQForm(options?.formType)) {
      return {
        location: {
          validator: {
            ...pvqLocationModelValidator,
            county: { presence: isSafeUnitedStates(value) },
          } as ValidateJSModel<LocationModel>,
        },
        presence: true,
      };
    }

    return {
      presence: true,
      location: {
        validator: offenseAddress,
        requireCity: value ? !value.county : true,
        requireCounty: value ? !value.city : true,
      },
    };
  },
  WasCited: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {}; // WasCited not used on PVQ Form
    }

    return {
      presence: true,
      hasValue: { validator: hasYesOrNo },
    };
  },
  CitedBy: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    if (isPVQForm(options?.formType)) {
      return { presence: true, hasValue: true };
    }

    if (attributes?.WasCited?.value === 'Yes') {
      return { presence: true, hasValue: true };
    }

    return {};
  },
  AgencyAddress: (value: DeepSafePartial<LocationModel>, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    if (isPVQForm(options?.formType)) {
      return {
        location: {
          requireStreet: isSafeUnitedStates(value),
          validator: pvqLocationModelValidator,
        },
        presence: true,
      };
    }

    if (checkValue(attributes?.WasCited, 'Yes')) {
      return {
        presence: true,
        location: {
          validator: offenseAddress,
          requireCity: value ? !value.county : true,
          requireCounty: value ? !value.city : true,
        },
      };
    }

    return {};
  },
  ArrestingAgencyAddress: (value: DeepSafePartial<LocationModel | undefined>, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.WasCharged?.value === 'Yes'
      && attributes?.OtherLawEnforcementAgency?.value === 'No') {
      return {
        location: {
          requireStreet: isSafeUnitedStates(value),
          validator: pvqLocationModelValidator,
        },
        presence: true,
      };
    }

    return {};
  },
  ArrestingAgencyName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.WasCharged?.value === 'Yes'
      && attributes?.OtherLawEnforcementAgency?.value === 'No') {
      return { hasValue: true };
    }

    return {};
  },
  WasCharged: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Has?.value === 'No') {
      return {};
    }
    return {
      // Validation is the same for both PVQ and non-PVQ
      presence: true,
      hasValue: { validator: hasYesOrNo },
    };
  },
  Explanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return (checkValue(attributes?.WasCharged, 'No') ? { presence: true, hasValue: true } : {});
  },
  CourtName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return checkValue(attributes?.WasCharged, 'Yes') ? { presence: true, hasValue: true } : {};
  },
  CourtAddress: (value: DeepSafePartial<LocationModel>, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (checkValue(attributes?.WasCharged, 'Yes')) {
      return {
        presence: true,
        location: {
          validator: offenseAddress,
          requireCity: value ? !value.county : true,
          requireCounty: value ? !value.city : true,
        },
      };
    }

    return {};
  },
  Charges: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.WasCharged?.value === 'Yes') {
        return {
          accordion: {
            offenseDate: attributes?.Date,
            validator: chargeValidator,
          },
          presence: true,
        };
      }

      return {};
    }

    if (attributes?.WasCharged?.value === 'Yes') {
      return {
        presence: true,
        accordion: { validator: chargeValidator, ignoreBranch: true },
      };
    }

    return {};
  },
  WasSentenced: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return (checkValue(attributes?.WasCharged, 'Yes')
      ? { presence: true, hasValue: { validator: hasYesOrNo } }
      : {});
  },
  AwaitingTrial: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.WasSentenced?.value === 'No') {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  AwaitingTrialExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.WasSentenced?.value === 'No') {
      return { presence: true, hasValue: true };
    }

    return {};
  },
  Sentence: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.WasSentenced?.value === 'Yes') {
      return { presence: true, model: { validator: legalSentenceValidator, offenseDate: attributes.Date } };
    }

    return {};
  },
  OtherLawEnforcementAgency: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.WasCharged?.value === 'Yes') {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  CourtStatus: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  CourtFinePayment: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes.CourtStatus?.value === 'No') {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  CourtFineAmount: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes.CourtStatus?.value === 'No'
      && attributes.CourtFinePayment?.value === 'Yes') {
      return { hasValue: true };
    }

    return {};
  },
  CourtFinePaidInFull: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes.CourtStatus?.value === 'No'
      && attributes.CourtFinePayment?.value === 'Yes') {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  CourtFinePaidInFullDate: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes.CourtStatus?.value === 'No'
      && attributes.CourtFinePayment?.value === 'Yes'
      && attributes.CourtFinePaidInFull?.value === 'Yes') {
      return {
        date: {
          earliest: getSafeDateTime(setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST) as DateTime).datetime,
          latest: DEFAULT_LATEST,
          requireDay: false,
          tooEarly: 'error.applicantMin',
        },
        presence: true,
      };
    }

    return {};
  },
  CourtFineNotPaidInFullExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes.CourtStatus?.value === 'No'
      && attributes.CourtFinePayment?.value === 'Yes'
      && attributes.CourtFinePaidInFull?.value === 'No') {
      return { hasValue: true };
    }

    return {};
  },
  CourtToAppear: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.CourtStatus?.value === 'Yes') {
      return { hasValue: true };
    }

    return {};
  },
  CourtToAppearAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.CourtStatus?.value === 'Yes') {
      return {
        location: { validator: pvqLocationModelValidator },
        presence: true,
      };
    }

    return {};
  },
};
